import React, { FC, Fragment, useState } from 'react'
import InputLabel from '@mui/material/InputLabel'
import { Controller, useForm } from 'react-hook-form'
import { REGEX_EMAIL_VALIDATION } from '../../../constants/regex'
import {
	Box,
	CircularProgress,
	FormHelperText,
	OutlinedInput,
} from '@mui/material'
import { history } from '../../../store'
import { PAGE_LOGIN } from '../../../constants/navigation'
import AppFormControl from '../../../components/AppForm/AppFormControl'
import LoginFormBody from '../common/LoginFormBody/LoginFormBody'
import LoginForgotPasswordSentEmailMsg from './LoginForgotPasswordSentEmailMsg'
import Button from '@mui/material/Button'
import { passwordChange } from '../../../store/reducers/auth/actions'
import { useDefaultComponentTools } from '../../../hooks/useDefaultComponentTools'

interface ILoginForgotPasswordForm {
	email: string
}

export const EMAIL_FIELD_NAME = 'email'

const LoginForgotPasswordForm: FC = () => {
	const [emailSent, setEmailSent] = useState<boolean>(false)
	const { loading, setLoading, alert, dispatch, isMounted } =
		useDefaultComponentTools()
	const {
		handleSubmit,
		control,
		getValues,
		formState: { errors, isValid },
	} = useForm<ILoginForgotPasswordForm>({
		mode: 'all',
		defaultValues: {
			[EMAIL_FIELD_NAME]: '',
		},
	})
	const email = getValues(EMAIL_FIELD_NAME)
	const emailError = errors[EMAIL_FIELD_NAME]?.message || ''
	const isSubmitButtonDisabled = loading || !isValid

	const handleClickCancel = (e: React.SyntheticEvent): void => {
		e.preventDefault()

		history.push(`${PAGE_LOGIN}`)
	}

	const onSubmit = async (data: ILoginForgotPasswordForm): Promise<void> => {
		try {
			setLoading(true)
			await dispatch(passwordChange(data.email))

			setEmailSent(true)
		} catch (e: any) {
			'message' in e && alert.error(e.message)
		} finally {
			isMounted() && setLoading(false)
		}
	}

	return (
		<Fragment>
			{!emailSent && (
				<LoginFormBody
					title={'Reset password'}
					subTitle={
						// eslint-disable-next-line max-len
						'Enter your email address and we will send you instructions to reset your password'
					}
					sx={{ paddingBottom: '2rem' }}
				>
					<form onSubmit={handleSubmit(onSubmit)}>
						<AppFormControl
							sx={{ mb: 7.2, mt: 2.3 }}
							color={'primary'}
							error={!!emailError}
						>
							<InputLabel shrink htmlFor={'login-email'}>
								Email
							</InputLabel>

							<Controller
								control={control}
								rules={{
									required: 'Email is required.',
									pattern: REGEX_EMAIL_VALIDATION,
								}}
								render={({ field }) => (
									<OutlinedInput
										id={'reset-login-email'}
										{...field}
										autoComplete={'email'}
										type={'email'}
										placeholder={'Email address'}
									/>
								)}
								name={EMAIL_FIELD_NAME}
							/>

							{!!emailError && (
								<FormHelperText id={'login-email'}>
									{emailError}
								</FormHelperText>
							)}
						</AppFormControl>

						<Box
							sx={{ display: 'flex', justifyContent: 'flex-end' }}
						>
							<Button
								size={'large'}
								variant='contained'
								color={'secondary'}
								type={'button'}
								onClick={handleClickCancel}
								disableElevation
							>
								Cancel
							</Button>

							<Button
								size={'large'}
								variant='contained'
								type={'submit'}
								color={'primary'}
								sx={{ ml: 2, minWidth: '6.7rem' }}
								disabled={isSubmitButtonDisabled}
							>
								{loading ? (
									<CircularProgress size={28} />
								) : (
									'Continue'
								)}
							</Button>
						</Box>
					</form>
				</LoginFormBody>
			)}

			{emailSent && <LoginForgotPasswordSentEmailMsg email={email} />}
		</Fragment>
	)
}

export default LoginForgotPasswordForm
